import type { AppExportApi, ExportCapability, ExportFormat, ExportResult } from '@yada/plugin-api';
import { todayLocalDate } from '@yada/types';

interface ExportEntry {
  appName: string;
  capabilities: ExportCapability[];
  handler: (format: ExportFormat, capabilityId?: string) => Promise<ExportResult>;
}

export class ExportRegistry {
  private entries = new Map<string, ExportEntry>();

  register(
    appId: string,
    appName: string,
    capabilities: ExportCapability[],
    handler: (format: ExportFormat, capabilityId?: string) => Promise<ExportResult>
  ): void {
    this.entries.set(appId, { appName, capabilities, handler });
  }

  unregister(appId: string): void {
    this.entries.delete(appId);
  }

  list(): Array<{ appId: string; appName: string; capabilities: ExportCapability[] }> {
    return Array.from(this.entries.entries()).map(([appId, entry]) => ({
      appId,
      appName: entry.appName,
      capabilities: entry.capabilities,
    }));
  }

  async export(appId: string, format: ExportFormat, capabilityId?: string): Promise<ExportResult> {
    const entry = this.entries.get(appId);
    if (!entry) throw new Error(`No export registered for app: ${appId}`);
    const cap = capabilityId
      ? entry.capabilities.find((c) => c.id === capabilityId)
      : entry.capabilities.find((c) => c.format === format);
    if (!cap) {
      throw new Error(
        capabilityId
          ? `Capability "${capabilityId}" not found in ${appId}`
          : `Format "${format}" not supported by ${appId}`
      );
    }
    return entry.handler(cap.format, cap.id);
  }

  private static stampFilename(filename: string): string {
    const today = todayLocalDate();
    const dot = filename.lastIndexOf('.');
    if (dot === -1) return `${filename}-${today}`;
    return `${filename.slice(0, dot)}-${today}${filename.slice(dot)}`;
  }

  /** Downloads the export and returns its warnings, so the caller can report an incomplete export. */
  async download(appId: string, format: ExportFormat, capabilityId?: string): Promise<string[]> {
    const result = await this.export(appId, format, capabilityId);
    const warnings = result.warnings ?? [];
    if (typeof window === 'undefined' || typeof document === 'undefined') return warnings;
    const blob =
      result.data instanceof Blob
        ? result.data
        : new Blob([result.data], { type: result.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = ExportRegistry.stampFilename(result.filename);
    a.click();
    URL.revokeObjectURL(url);
    return warnings;
  }

  createApi(appId: string, appName: string): AppExportApi {
    return {
      register: (capabilities, handler) =>
        this.register(
          appId,
          appName,
          capabilities,
          handler as (format: ExportFormat, capabilityId?: string) => Promise<ExportResult>
        ),
    };
  }
}
